import React, { FC, useEffect, useState } from 'react'
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import PlaylistComponent from '@components/playlist/playlist.component'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import {
  AISlotModel,
  AiSlotState,
  selectAiSlotById,
  selectIsAiSlotIdInProgress,
} from '@store/slices/aiSlot.slice'
import { Card, Col } from 'react-bootstrap'
import { toHourMinString } from '@shared/tools/time.tools'
import {
  AIMetadataModel,
  fetchMetadataById,
  selectAIMetadataById,
  selectIsAIMetadataIdInProgress,
} from '@store/slices/aiMeta.slice'
import {
  changeSlotDefaultVoice,
  setSlotBgAudio,
  setSlotTemplate,
} from '@store/thunk/aiSlot.thunk'
import AppButton from '@components/shared/appButton'
import {
  ArrowsAngleContract,
  ArrowsAngleExpand,
  CloudArrowUp,
  CloudCheck,
  GearFill,
  MusicNote,
  PeopleFill,
  PersonLinesFill,
} from 'react-bootstrap-icons'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import { AppSelectComponent } from '@components/shared/appSelect.component'
import {
  fetchAllVoices,
  selectAllVoices,
  selectVoiceLoading,
} from '@store/slices/voice.slice'
import { toast } from 'react-toastify'
import SlotStatusComponent from '@components/slot/slotStatus.component'
import {
  fetchAllRegions,
  RegionModel,
  selectAllRegions,
  selectRegionLoading,
} from '@store/slices/region.slice'
import {
  fetchAllNarrations,
  NarrationModel,
  selectNarrationLoading,
  selectNarrationsByIds,
} from '@store/slices/narration.slice'
import { DjangoFilter } from '@store/slices/generic.slice'
import {
  handleNarrateSlot,
  handlePublishAllForSlot,
  handlePublishUpdatedForSlot,
  handleTranscribeSlot,
} from '@services/aislot.service'
import { getSlotStateName } from '@shared/tools/enum.tools'
import {
  selectAllTemplates,
  selectTemplateLoading,
  TemplateModel,
} from '@store/slices/template.slice'
import { AudioType, TemplateType } from '@shared/enums'
import {
  AudioModel,
  selectAllAudios,
  selectAudioLoading,
} from '@store/slices/audio.slice'
import { fetchSsmlTemplates } from '@services/template.service'
import { fetchBgAudios } from '@services/audio.service'
import NarrationComponent from '@components/narration/narration.component'
import { playTmpAudio } from '@shared/tools/audioTools'

const SlotComponent: React.FC = () => {
  return (
    <div className={'d-flex gap-2 my-2'}>
      <Col className={'w-50'}>
        <PlaylistComponent></PlaylistComponent>
      </Col>
      <Col className={'w-50'}>
        <Routes>
          <Route path="/region/:regionId/*" element={<Slot />} />
          <Route path="" element={<Slot />} />
        </Routes>
      </Col>
    </div>
  )
}

const Slot: React.FC = () => {
  const { regionId, slotId, playlistId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const aiSlot: AISlotModel | null = useSelector((state: RootState) =>
    slotId ? selectAiSlotById(state, slotId) : null
  )
  const slotLoading = useSelector((state: RootState) =>
    !!slotId ? selectIsAiSlotIdInProgress(state, slotId) : null
  )
  const aiMetadata: AIMetadataModel | null = useSelector((state: RootState) =>
    aiSlot?.aiEntryMetadata
      ? selectAIMetadataById(state, aiSlot.aiEntryMetadata)
      : null
  )
  const aiMetadataLoading = useSelector((state: RootState) =>
    aiSlot?.aiEntryMetadata
      ? selectIsAIMetadataIdInProgress(state, aiSlot.aiEntryMetadata)
      : null
  )
  const regions = useSelector(selectAllRegions)
  const regionsLoading = useSelector(selectRegionLoading)
  const templates = useSelector(selectAllTemplates)
  const isTemplatesLoading = useSelector(selectTemplateLoading)
  const ssmlTemplates: TemplateModel[] = templates.filter(
    (template) => template.templateType === TemplateType.SSML_TEMPLATE
  )
  const audios = useSelector(selectAllAudios)
  const isAudioLoading = useSelector(selectAudioLoading)
  const bgAudios: AudioModel[] = audios.filter(
    (audio) => audio.audioType === AudioType.BACKGROUND_AUDIO
  )
  const [slotDefaultVoiceSelect, setSlotDefaultVoiceSelect] =
    useState<EntityId>()
  const [slotTemplateSelect, setSlotTemplateSelect] = useState<EntityId>()
  const [slotBgAudioSelect, setSlotBgAudioSelect] = useState<EntityId>()
  const voices = useSelector(selectAllVoices)
  const voiceLoading = useSelector(selectVoiceLoading)
  const voiceOptions = voices.map((voice) => ({
    id: voice.id,
    label: voice.name,
  }))
  const bgAudioOptions = bgAudios.map((audio) => ({
    id: audio.id,
    label: audio.name,
  }))
  const ssmlTemplateOptions = ssmlTemplates.map((template) => ({
    id: template.id,
    label: template.name,
  }))
  const narrationsLoading = useSelector(selectNarrationLoading)
  const slotNarrations: NarrationModel[] = useSelector((state: RootState) =>
    aiSlot ? selectNarrationsByIds(state, aiSlot.narrations) : []
  )

  const [isCardBodyVisible, setCardBodyVisible] = useState(false)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const toggleCardBodyVisibility = () => setCardBodyVisible(!isCardBodyVisible)

  const onSelectVoiceChange = (voiceId: EntityId | null) => {
    if (voiceId && aiSlot) {
      dispatch(
        changeSlotDefaultVoice({
          slotId: aiSlot.id,
          voiceId: voiceId,
        })
      )
        .then(unwrapResult)
        .then(() =>
          dispatch(
            fetchAllNarrations(
              new DjangoFilter<AISlotModel>({ ids: [aiSlot.narrations] })
            )
          )
        )
    } else {
      toast.warning('Hlas musí být vybrán')
    }
  }

  const onSelectBgAudioChange = (bgAudioId: EntityId | null) => {
    if (bgAudioId && aiSlot) {
      dispatch(setSlotBgAudio({ slotId: aiSlot.id, bgAudioId: bgAudioId }))
    } else {
      toast.warning('BgAudio musí být vybrán')
    }
  }

  const onSelectSsmlTemplateChange = (ssmlTemplateId: EntityId | null) => {
    if (ssmlTemplateId && aiSlot) {
      dispatch(
        setSlotTemplate({ slotId: aiSlot.id, templateId: ssmlTemplateId })
      )
    } else {
      toast.warning('Šablona musí být vybrána')
    }
  }

  useEffect(() => {
    if (voices?.length == 0 && !voiceLoading) {
      dispatch(fetchAllVoices())
    }
    if (
      voices.length > 0 &&
      aiSlot &&
      slotDefaultVoiceSelect != aiSlot.defaultVoice
    ) {
      setSlotDefaultVoiceSelect(aiSlot.defaultVoice)
    }
  }, [voices, aiSlot?.defaultVoice])

  useEffect(() => {
    if (ssmlTemplates?.length == 0 && !isTemplatesLoading) {
      fetchSsmlTemplates().then()
    } else if (
      ssmlTemplates.length > 0 &&
      aiSlot &&
      slotTemplateSelect != aiSlot.template
    ) {
      setSlotTemplateSelect(aiSlot.template)
    }
  }, [templates, aiSlot])

  useEffect(() => {
    if (bgAudios?.length == 0 && !isAudioLoading) {
      fetchBgAudios().then()
    } else if (
      bgAudios.length > 0 &&
      aiSlot &&
      slotBgAudioSelect != aiSlot.bgAudio
    ) {
      setSlotBgAudioSelect(aiSlot.bgAudio)
    }
  }, [audios, aiSlot])

  useEffect(() => {
    if (aiSlot?.aiEntryMetadata && !aiMetadata && !aiMetadataLoading)
      dispatch(fetchMetadataById(aiSlot.aiEntryMetadata))
  }, [aiSlot?.aiEntryMetadata])

  useEffect(() => {
    if (regions?.length == 0 && !regionsLoading) dispatch(fetchAllRegions())
  }, [regions])

  useEffect(() => {
    if (!regionId && regions.length > 0) {
      navigateToDefaultRegion()
    }
  }, [aiSlot, regionId, regions])

  useEffect(() => {
    if (
      aiSlot?.narrations &&
      aiSlot?.narrations.length != 0 &&
      (!slotNarrations ||
        slotNarrations.length == 0 ||
        slotNarrations.length != aiSlot.narrations.length) &&
      !narrationsLoading
    ) {
      fetchNarrationsForSlot()
    }
  }, [slotNarrations, aiSlot?.narrations])

  useEffect(() => {
    if (
      slotTemplateSelect &&
      !slotBgAudioSelect &&
      aiSlot &&
      !aiSlot.bgAudio &&
      bgAudios.length > 0
    ) {
      setDefaultBgAudio()
    }
  }, [slotTemplateSelect, bgAudios, aiSlot])

  useEffect(() => {
    if (aiSlot && !aiSlot.template && templates.length > 0 && !slotLoading) {
      setDefaultTemplate()
    }
  }, [aiSlot, templates])

  const setDefaultTemplate = () => {
    const defaultTemplate: TemplateModel | undefined = ssmlTemplates.find(
      (template) =>
        template.isDefault &&
        template.templateType == TemplateType.SSML_TEMPLATE
    )
    if (defaultTemplate) {
      onSelectSsmlTemplateChange(defaultTemplate.id)
    }
  }

  const setDefaultBgAudio = () => {
    const template: TemplateModel | undefined = ssmlTemplates.find(
      (template) => template.id == slotTemplateSelect
    )
    if (template && template.bgAudioSelect && template.defaultBgAudio) {
      const bgAudio: AudioModel | undefined = bgAudios.find(
        (audio) => audio.id == template.defaultBgAudio
      )
      if (bgAudio) {
        console.log(`seting bg audio for tempalte ${template.id}`)
        onSelectBgAudioChange(bgAudio.id)
      }
    }
  }

  const fetchNarrationsForSlot = () => {
    const filter = new DjangoFilter<AISlotModel>()
    aiSlot?.narrations && filter.addIdFilter(aiSlot.narrations)
    dispatch(fetchAllNarrations(filter))
  }

  const navigateToDefaultRegion = () => {
    const defaultRegion = regions.find(
      (region: RegionModel) => region.isDefault
    )
    if (defaultRegion) {
      navigate(
        `/playlist/${playlistId}/slot/${slotId}/region/${defaultRegion?.id}`
      )
    }
  }

  const onNarrateClick = () => {
    if (aiSlot && playlistId) return handleNarrateSlot(aiSlot, playlistId)
  }

  const onTranscribeClick = () => {
    if (aiSlot && playlistId) return handleTranscribeSlot(aiSlot, playlistId)
  }

  const onPublishUpdated = () => {
    if (aiSlot && playlistId)
      return handlePublishUpdatedForSlot(aiSlot, playlistId)
  }

  const onPublishAll = () => {
    if (aiSlot && playlistId) return handlePublishAllForSlot(aiSlot, playlistId)
  }

  const onPlayBgAudioClick = () => {
    const audio = bgAudios.find((audio) => audio.id == slotBgAudioSelect)
    if (audio) {
      setIsAudioPlaying(true)
      playTmpAudio(audio.publicPath)
        .catch((e) => {
          console.error(e)
          toast.error('Audio se nepodařilo nalézt')
        })
        .finally(() => setIsAudioPlaying(false))
    }
  }

  const isPublishUpdatedDisabled = (): boolean => {
    return (
      !aiSlot ||
      !slotNarrations.some(
        (narration) =>
          narration.audioUrl &&
          narration.audioCreated &&
          narration.uploaded &&
          new Date(narration.uploaded) < new Date(narration.audioCreated)
      )
    )
  }

  const isPublishAllDisabled = (): boolean => {
    return (
      !aiSlot ||
      !slotNarrations.some(
        (narration) =>
          narration.audioUrl &&
          narration.audioCreated &&
          (!narration.uploaded ||
            new Date(narration.uploaded) < new Date(narration.audioCreated))
      )
    )
  }

  const isBgAudioSelectShown = (): boolean => {
    const selectedTemplate: TemplateModel | undefined = templates.find(
      (template) => template.id === slotTemplateSelect
    )
    return !!selectedTemplate && selectedTemplate.bgAudioSelect
  }

  const TemplatePlay: FC = () => {
    return (
      <AppButton
        onClick={onPlayBgAudioClick}
        buttonChild={<MusicNote />}
        isLoading={isAudioPlaying}
        disabled={!slotBgAudioSelect}
        buttonVariant={'primary'}
      />
    )
  }

  const TemplateSelect: FC = () => {
    return (
      <div className={'d-flex gap-2 align-items-center'}>
        Šablona&emsp;
        <AppSelectComponent
          onSelectChange={onSelectSsmlTemplateChange}
          options={ssmlTemplateOptions}
          valueId={slotTemplateSelect ? slotTemplateSelect : undefined}
          placeholder={'Šablona'}
          className={'p-0'}
        ></AppSelectComponent>
      </div>
    )
  }

  const BgAudioSelect: FC = () => {
    return (
      <div className={'d-flex gap-2 align-items-center'}>
        Podkres&emsp;
        <AppSelectComponent
          onSelectChange={onSelectBgAudioChange}
          options={bgAudioOptions}
          valueId={slotBgAudioSelect ? slotBgAudioSelect : undefined}
          placeholder={'Podkres'}
          className={'p-0'}
        ></AppSelectComponent>
      </div>
    )
  }

  const VoiceSelect: FC = () => {
    return (
      <div className={'d-flex gap-2 align-items-center'}>
        Přednastavený hlas&emsp;
        <AppSelectComponent
          onSelectChange={onSelectVoiceChange}
          options={voiceOptions}
          valueId={slotDefaultVoiceSelect ? slotDefaultVoiceSelect : undefined}
          placeholder={'Vyberte hlas'}
          className={'p-0'}
        ></AppSelectComponent>
      </div>
    )
  }

  const cardBodyWrapperStyle = {
    overflow: isCardBodyVisible ? 'visible' : 'hidden',
    display: 'block',
    transition: 'max-height 0.5s ease-out',
    maxHeight: isCardBodyVisible ? 'none' : '0', // Adjust maxHeight as needed
  } as React.CSSProperties

  return (
    <div className={'d-flex flex-column gap-2'}>
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="p-0 m-0">
              {aiMetadata && (
                <span>{`${aiMetadata?.aiLinkType.toUpperCase()}`}</span>
              )}
            </h4>
            <div className="d-flex gap-2">
              <AppButton
                tooltip={
                  isCardBodyVisible ? 'Schovat detaily' : 'Zobrazit detaily'
                }
                buttonVariant={'secondary'}
                buttonChild={
                  isCardBodyVisible ? (
                    <ArrowsAngleContract />
                  ) : (
                    <ArrowsAngleExpand />
                  )
                }
                onClick={toggleCardBodyVisibility}
              ></AppButton>
              {aiMetadata && (
                <AppButton
                  onClick={() =>
                    navigate(`/playlist/${playlistId}/outline/${aiSlot?.id}`)
                  }
                  buttonChild={<PersonLinesFill />}
                  tooltip="Přejít na dramaturga"
                  buttonVariant={'secondary'}
                ></AppButton>
              )}
              {aiMetadata && (
                <>
                  <AppButton
                    isLoading={slotLoading}
                    tooltip={'Přegenerovat slot'}
                    buttonVariant={'primary'}
                    buttonChild={<GearFill />}
                    onClick={onNarrateClick}
                    disabled={!aiMetadata}
                  ></AppButton>
                  <AppButton
                    isLoading={slotLoading}
                    tooltip={'Vygenerovat dialog'}
                    buttonVariant={'primary'}
                    buttonChild={<PeopleFill />}
                    onClick={onTranscribeClick}
                    disabled={!aiMetadata}
                  ></AppButton>
                </>
              )}
              <AppButton
                isLoading={slotLoading}
                tooltip={'Publikovat aktualizované'}
                buttonVariant={'success'}
                buttonChild={<CloudCheck />}
                onClick={onPublishUpdated}
                disabled={isPublishUpdatedDisabled()}
              ></AppButton>
              <AppButton
                isLoading={slotLoading}
                tooltip={'Publikovat vše'}
                buttonVariant={'success'}
                buttonChild={<CloudArrowUp />}
                onClick={onPublishAll}
                disabled={isPublishAllDisabled()}
              ></AppButton>
            </div>
          </div>
        </Card.Header>
        <div style={cardBodyWrapperStyle}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column gap-2">
                <span>
                  Plid <strong>{aiSlot?.plid}</strong>
                </span>
                <div>
                  <span>
                    Čas&nbsp;
                    <strong>
                      {aiSlot && toHourMinString(aiSlot.exactTime)}
                    </strong>
                    &nbsp;
                  </span>
                  <span className="text-secondary">
                    {aiSlot && aiSlot.exactTime}
                  </span>
                </div>
                <div>
                  Stav&nbsp;
                  <strong
                    className={
                      aiSlot && aiSlot.state < AiSlotState.TEXT
                        ? aiSlot.state < AiSlotState.AUDIO
                          ? 'text-success'
                          : 'text-primary'
                        : ''
                    }
                  >
                    {aiSlot?.state != null || aiSlot?.state != undefined
                      ? getSlotStateName(aiSlot.state)
                      : ''}
                  </strong>
                </div>
                <VoiceSelect />
                <div className={'d-flex gap-2 justify-content-between'}>
                  <TemplateSelect />
                  {isBgAudioSelectShown() && (
                    <>
                      <BgAudioSelect />
                      <TemplatePlay />
                    </>
                  )}
                </div>
                {slotId && <SlotStatusComponent aiSlotId={slotId} />}
              </div>
            </div>
          </Card.Body>
        </div>
      </Card>
      <NarrationComponent />
    </div>
  )
}

export default SlotComponent
